/**
 * Open board browse controls — the rank / entry / size options behind the filter chips and the
 * sheet, plus the sort order. Values live here so the chip label, the sheet option and the
 * predicate can never drift apart. specs/open-challenges AC-17.
 */

import { MAX_GAMES_PER_CHALLENGE } from '../../create/constants/create.constants';
import type {
  OpenBoardBrowseSort,
  OpenBoardBrowseState,
  OpenBoardSizeFilter,
} from '../types/openBoard.types';

export interface OpenBoardRankOption {
  id: string;
  label: string;
  /** Minimum rank index (1..10) a poster must hold. null = no floor. */
  rankFloor: number | null;
}

/**
 * Rank floors, not bands: "Veteran +" reads as "worth my time" far better than "Veteran only", and
 * a band would hide the strongest opponents, which is the opposite of what the board is for.
 * Indices match the rank registry (1 Rookie … 10 GOAT).
 */
export const OPEN_BOARD_RANK_OPTIONS: readonly OpenBoardRankOption[] = [
  { id: 'any', label: 'Any rank', rankFloor: null },
  { id: 'contender', label: 'Contender +', rankFloor: 3 },
  { id: 'veteran', label: 'Veteran +', rankFloor: 5 },
  { id: 'champion', label: 'Champion +', rankFloor: 7 },
  { id: 'legend', label: 'Legend +', rankFloor: 9 },
] as const;

export interface OpenBoardAmountOption {
  id: string;
  label: string;
  minStake: number | null;
  maxStake: number | null;
}

/** Entry-size buckets. Open-ended at the top so a whale challenge is never unreachable. */
export const OPEN_BOARD_AMOUNT_OPTIONS: readonly OpenBoardAmountOption[] = [
  { id: 'any', label: 'Any amount', minStake: null, maxStake: null },
  { id: 'low', label: '0 – 250', minStake: 0, maxStake: 250 },
  { id: 'mid', label: '250 – 1,000', minStake: 250, maxStake: 1000 },
  { id: 'high', label: '1,000 – 5,000', minStake: 1000, maxStake: 5000 },
  { id: 'whale', label: '5,000 +', minStake: 5000, maxStake: null },
] as const;

/**
 * Pick count at or below which a challenge is a "quick" slate. Above it the slate is larger than
 * the free allowance, which is what makes it a Pro-size challenge — so the boundary is the
 * allowance itself, never a second number that could drift away from it.
 */
export const OPEN_BOARD_QUICK_MAX_PICKS = MAX_GAMES_PER_CHALLENGE;

export interface OpenBoardSizeOption {
  id: OpenBoardSizeFilter;
  label: string;
  hint: string;
}

/**
 * Challenge SIZE, not who posted it: the board is browsed by how big a slate you are signing up
 * for. "Big" is anything past the free pick allowance — the crown marks it as a Pro-size slate.
 */
export const OPEN_BOARD_SIZE_OPTIONS: readonly OpenBoardSizeOption[] = [
  { id: 'all', label: 'Any size', hint: 'all challenges' },
  { id: 'quick', label: `Up to ${OPEN_BOARD_QUICK_MAX_PICKS}`, hint: 'quick slate' },
  { id: 'big', label: `${OPEN_BOARD_QUICK_MAX_PICKS + 1}+ picks`, hint: 'Pro-size' },
] as const;

export interface OpenBoardSortOption {
  id: OpenBoardBrowseSort;
  label: string;
}

/** Tapping the sort control cycles this list in order. */
export const OPEN_BOARD_SORT_OPTIONS: readonly OpenBoardSortOption[] = [
  { id: 'closing-soonest', label: 'Closing soon' },
  { id: 'highest-stake', label: 'Highest entry' },
  { id: 'top-rank', label: 'Top rank' },
  { id: 'most-picks', label: 'Most picks' },
] as const;

export const OPEN_BOARD_DEFAULT_BROWSE: OpenBoardBrowseState = {
  rankFloor: null,
  minStake: null,
  maxStake: null,
  size: 'all',
  sort: 'closing-soonest',
};

/** Minutes-to-kickoff at or under which a card is flagged as closing soon (red clock + border). */
export const OPEN_BOARD_URGENT_MINUTES = 20;

/**
 * Payout multiple shown as "PAYOUT": stake x2 minus the 5% house cut on the pot — i.e. the total
 * credited to the winner, which includes their own stake back. It is deliberately NOT labelled
 * "TO WIN" with a leading "+": the profit on a 1,000 entry is 900, not 1,900, and a "+" under
 * "TO WIN" reads as profit. Mirrors settlementEconomy.helper.js (pot x HOUSE_PAYOUT_BPS).
 */
export const OPEN_BOARD_PAYOUT_MULTIPLIER = 1.9;

export const OPEN_BOARD_TEST_IDS = {
  filterButton: 'open-board-filter-button',
  filterBadge: 'open-board-filter-badge',
  rankChip: 'open-board-chip-rank',
  amountChip: 'open-board-chip-amount',
  sizeChip: 'open-board-chip-size',
  sortControl: 'open-board-sort',
  resultLine: 'open-board-result-line',
  sheet: 'open-board-filter-sheet',
  sheetApply: 'open-board-filter-apply',
  sheetReset: 'open-board-filter-reset',
  clearFilters: 'open-board-clear-filters',
} as const;
